import { apply, getAppliedSeq } from './store';
import type { ClientGameState, GameEvent } from '../shared/types';

/**
 * The server writes a heartbeat every 2s. Silence past two beats (plus slack
 * for one retransmit) means the socket is dead: that is a promised event that
 * failed to arrive, not a poll of game state. It is also the ceiling on how
 * long a dead stream can hold the board frozen, which is why it is short.
 */
const HEARTBEAT_MS = 2000;
const PROMISED_EVENT_MS = HEARTBEAT_MS * 2 + 500;

let source: EventSource | null = null;
let wanted = false;
let gameId = '';
let playerId = 0;
let retryMs = 250;
let promisedTimer: number | null = null;
let reconnectTimer: number | null = null;
/** Wall-clock time of the last byte of data (or open) on the current stream. */
let lastActivityAt = 0;
let openedAt = 0;

function armPromisedEvent(): void {
  if (promisedTimer !== null) window.clearTimeout(promisedTimer);
  lastActivityAt = Date.now();
  promisedTimer = window.setTimeout(() => {
    promisedTimer = null;
    // Heartbeats stopped. Rebuild exactly once; avoid racing two reconnect
    // timers from both this timeout and the onerror close callback.
    report('stream-silent', `no data for ${Date.now() - lastActivityAt}ms`);
    rebuildNow();
  }, PROMISED_EVENT_MS);
}

/** Tear the current stream down and open a fresh one right away. */
export function rebuildNow(): void {
  if (!wanted) return;
  source?.close();
  source = null;
  if (reconnectTimer !== null) {
    window.clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  retryMs = 250;
  connect(gameId, playerId);
}

function reconnect(): void {
  if (!wanted) return;
  if (reconnectTimer !== null) return;
  const wait = retryMs;
  retryMs = Math.min(retryMs * 2, 5000);
  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null;
    if (wanted) connect(gameId, playerId);
  }, wait);
}

export function connect(id: string, pid: number): void {
  wanted = true;
  gameId = id;
  playerId = pid;
  if (reconnectTimer !== null) {
    window.clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (source) {
    source.close();
    source = null;
  }
  openedAt = Date.now();
  source = new EventSource(`/api/game/${id}/events?playerId=${pid}`);
  // Opening counts as activity: a stream slow to establish must not be
  // measured against the previous stream's silence.
  armPromisedEvent();

  source.onopen = () => {
    armPromisedEvent();
  };

  source.onmessage = (e: MessageEvent) => {
    if (!e.data || !wanted) return;
    armPromisedEvent();
    retryMs = 250;
    let payload: {
      seq?: number;
      gameState?: ClientGameState;
      events?: GameEvent[];
      heartbeat?: boolean;
    };
    try {
      payload = JSON.parse(e.data as string);
    } catch {
      return;
    }
    if (payload.heartbeat) return;
    if (payload.gameState && typeof payload.seq === 'number') {
      apply({
        seq: payload.seq,
        gameState: payload.gameState,
        events: payload.events ?? [],
      });
    }
  };

  source.onerror = () => {
    if (!wanted) return;
    source?.close();
    source = null;
    reconnect();
  };
}

export function disconnect(): void {
  wanted = false;
  if (reconnectTimer !== null) {
    window.clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (promisedTimer !== null) {
    window.clearTimeout(promisedTimer);
    promisedTimer = null;
  }
  source?.close();
  source = null;
}

/**
 * True when the stream cannot be trusted: closed, still connecting long past
 * what a handshake needs, or open but silent past a heartbeat. Background
 * tabs and sleeping phones leave `readyState` at OPEN on a socket the network
 * dropped long ago, so state alone is not enough.
 */
function streamLooksDead(): boolean {
  if (!source) return true;
  if (source.readyState === EventSource.CLOSED) return true;
  const idle = Date.now() - lastActivityAt;
  if (source.readyState === EventSource.CONNECTING) return idle > HEARTBEAT_MS;
  // One beat can arrive late behind a retransmit without the stream being
  // dead; only treat it as gone once a second beat is also overdue.
  return idle > HEARTBEAT_MS * 1.5 + 500;
}

/**
 * Fired when the page comes back from wherever it has been. If the stream
 * went quiet while we were away, rebuild it now instead of waiting out the
 * silence timer (which the browser may also have throttled).
 */
function onWake(): void {
  if (!wanted || document.hidden) return;
  if (reconnectTimer !== null || streamLooksDead()) {
    report('wake-rebuild', `readyState=${source?.readyState ?? 'none'} idle=${Date.now() - lastActivityAt}ms`);
    rebuildNow();
  }
}

export function watchVisibility(): void {
  document.addEventListener('visibilitychange', onWake);
  window.addEventListener('focus', onWake);
  window.addEventListener('pageshow', onWake);
  // The network is back. Whatever backoff the last failure left us with is
  // stale now; go immediately.
  window.addEventListener('online', () => {
    if (!wanted) return;
    report('online-rebuild', `opened ${Date.now() - openedAt}ms ago`);
    rebuildNow();
  });
}

/**
 * One line in the server log when the client has to unstick itself. From the
 * server a frozen board and a slow player look identical; this is what tells
 * them apart after the fact. Fire and forget: never awaited, never retried.
 */
export function report(kind: string, detail: string): void {
  if (!gameId) return;
  const body = JSON.stringify({
    game: gameId,
    kind,
    detail: `${detail} seq=${getAppliedSeq()}`,
  });
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/api/client-event', new Blob([body], { type: 'application/json' }));
      return;
    }
    void fetch('/api/client-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => { /* best effort */ });
  } catch { /* best effort */ }
}
